import { useEffect, useRef, useState } from 'react';
import { ContentCard } from '../../components/ContentCard/ContentCard';
import { Button } from '../../components/Form/Button';
import { TextInput } from '../../components/Form/TextInput';
import { Icon, close } from '../../components/Icon/Icon';
import { InputGroup } from '../../components/InputGroup/InputGroup';
import { Modal } from '../../components/Modal/Modal';
import { useConfig } from '../../config';
import { useTranslation } from '../../i18n/i18n';
import { ApiList } from '../../types';
import { AlertMessages, useAlerts } from '../AlertMessages/AlertMessages';
import './CreateSubscriptionModal.css';
import { renameList } from './renameList';

/*
 * The draft shows its name as the first of five steps, with the name itself as the answer, so
 * changing it is a step of its own rather than a heading that turns into a field when clicked.
 */
export const RenameSubscriptionModal = ({
	list,
	show,
	onClose,
	onRenamed,
}: {
	list: ApiList;
	show: boolean;
	onClose: () => void;
	onRenamed: () => void;
}): JSX.Element => {
	const { api } = useConfig();
	const t = { ...useTranslation().subscriptions, generic: useTranslation().generic };
	const [name, setName] = useState(list.name);
	const [saving, setSaving] = useState(false);
	const alerts = useAlerts();
	/*
	 * Which opening of the dialog we are on. A rename can still be in flight when the customer
	 * closes it and opens it again, and when it lands it must not close the one they are typing in
	 * now - the name itself is saved either way, so only the closing waits on this.
	 */
	const session = useRef(0);

	// whatever was typed and left behind belongs to the opening it was typed in
	useEffect(() => {
		if (show) {
			session.current += 1;
			setName(list.name);
			alerts.clear();
		}
	}, [show]);

	// walking away ends this opening, so a rename still in flight leads nowhere
	const dismiss = () => {
		session.current += 1;
		onClose();
	};

	const save = async () => {
		const startedIn = session.current;

		alerts.clear();
		setSaving(true);

		try {
			await renameList(api, list.id, name);
		} catch (e) {
			alerts.set({ type: 'error', message: t.couldNotSave });
			return;
		} finally {
			setSaving(false);
		}

		onRenamed();

		if (session.current === startedIn) onClose();
	};

	return (
		<Modal className="create-subscription-modal" show={show} disableBodyScroll onDismiss={dismiss} toggle={dismiss}>
			<ContentCard className="create-subscription-content">
				<div className="modal-header">
					<div className="modal-header-title">{t.changeName}</div>
					<button className="close-btn" onClick={dismiss}>
						<Icon shape={{ ...close, fill: 'black' }} size={20} />
					</button>
				</div>
				<div className="modal-body">
					<InputGroup>
						<TextInput placeholder={t.name} value={name} onChange={(e) => setName(e.target.value)} />
						<Button disabled={saving || name === ''} onClick={save}>
							{saving ? t.generic.saving : t.generic.save}
						</Button>
					</InputGroup>
					{name === '' && <p className="name-example">{t.nameExample}</p>}
					<AlertMessages alerts={alerts} />
				</div>
			</ContentCard>
		</Modal>
	);
};
